import type { Auftrag } from '@awf/shared';
import { hatFreigabe, TYPEN_MIT_FREIGABE, type TypMitFreigabe } from '../auftragstypen.js';
import { WARTE_STATUS } from '../runs/status.js';
import type { LinienSymbolName } from './linien-symbol.js';

/** Zaehler gibt es fuer jeden Typ, auf den ein Mensch warten kann (Typ-Register). */
export type ZaehlerTyp = TypMitFreigabe;

export interface NavPunkt {
  schluessel: string;
  text: string;
  pfad: string;
  /** Suchparameter, die fuer diesen Punkt stimmen muessen (URL-Vertrag der Auftragsliste). */
  suche?: Readonly<Record<string, string>>;
  /** `'alle'` zaehlt die Wartenden aller Typen zusammen. */
  zaehler?: ZaehlerTyp | 'alle';
}

export interface NavGruppe {
  schluessel: string;
  text: string;
  symbol: LinienSymbolName;
  punkte: readonly NavPunkt[];
}

export type NavEintrag =
  | { art: 'punkt'; symbol: LinienSymbolName; punkt: NavPunkt }
  | { art: 'gruppe'; gruppe: NavGruppe };

/**
 * Gliederung des Mock-ups, aber nur, was es gibt (Spec A, Abschnitt 2): kein Admin, keine
 * Rollen. Eigene Review-Punkte gibt es nicht mehr (Rueckmeldung 2026-10-13): Wartende Auftraege
 * zeigt die Uebersicht, ihre Zahl steht am Punkt "Übersicht".
 */
export const NAVIGATION: readonly NavEintrag[] = [
  {
    art: 'punkt',
    symbol: 'dashboard',
    punkt: { schluessel: 'dashboard', text: 'Übersicht', pfad: '/', zaehler: 'alle' },
  },
  {
    art: 'punkt',
    symbol: 'plus',
    punkt: { schluessel: 'neu', text: 'Neuer Auftrag', pfad: '/neu' },
  },
  {
    art: 'gruppe',
    gruppe: {
      schluessel: 'ratgeber',
      text: 'Ratgeber',
      symbol: 'file',
      punkte: [
        { schluessel: 'ratgeber-erstellung', text: 'Erstellung', pfad: '/neu/ratgeber' },
        { schluessel: 'ratgeber-analyse', text: 'Analyse', pfad: '/neu/analyse' },
      ],
    },
  },
  // Ohne Review-Punkte bleibt bei Produktseiten und Bildern je ein Einstieg: direkt als Punkt
  // statt als Gruppe mit nur einem Unterpunkt (Rueckmeldung 2026-10-13).
  {
    art: 'punkt',
    symbol: 'globe',
    punkt: { schluessel: 'pdp-analyse', text: 'Produktseiten', pfad: '/neu/pdp' },
  },
  {
    art: 'punkt',
    symbol: 'image',
    punkt: { schluessel: 'bilder-erstellung', text: 'Bilder', pfad: '/neu/bilder' },
  },
];

export function zielVon(punkt: NavPunkt): string {
  if (!punkt.suche) return punkt.pfad;
  return `${punkt.pfad}?${new URLSearchParams(punkt.suche).toString()}`;
}

function allePunkte(): NavPunkt[] {
  return NAVIGATION.flatMap((e) => (e.art === 'punkt' ? [e.punkt] : [...e.gruppe.punkte]));
}

/**
 * Welcher Punkt ist aktiv? Passen mehrere auf dieselbe Route, gewinnt der mit den meisten
 * passenden Suchparametern. Detail- und
 * Freigabeseiten gehoeren zur Uebersicht: Ihren Typ kennt die Adresse nicht.
 */
export function aktiverPunkt(pfad: string, suche: string): string | null {
  if (pfad.startsWith('/auftraege/')) return 'dashboard';
  const parameter = new URLSearchParams(suche);
  const passende = allePunkte().filter(
    (p) =>
      p.pfad === pfad && Object.entries(p.suche ?? {}).every(([k, v]) => parameter.get(k) === v),
  );
  const genauester = passende.reduce<NavPunkt | undefined>(
    (bisher, p) =>
      !bisher || Object.keys(p.suche ?? {}).length > Object.keys(bisher.suche ?? {}).length
        ? p
        : bisher,
    undefined,
  );
  return genauester?.schluessel ?? null;
}

export type WartendeZahlen = Record<ZaehlerTyp, number>;

export function anzahlFuer(zaehler: NavPunkt['zaehler'], zahlen: WartendeZahlen): number {
  if (zaehler === undefined) return 0;
  if (zaehler === 'alle') return Object.values(zahlen).reduce((n, z) => n + z, 0);
  return zahlen[zaehler];
}

/**
 * Die Wartenden-Abfrage liefert nur wartende Laeufe. Der Status wird trotzdem geprueft,
 * damit die Zahl nicht still falsch wird, falls die Quelle einmal mehr liefert.
 */
export function zaehleWartende(auftraege: readonly Auftrag[]): WartendeZahlen {
  const zahlen = Object.fromEntries(TYPEN_MIT_FREIGABE.map((t) => [t, 0])) as WartendeZahlen;
  for (const a of auftraege) {
    if (!WARTE_STATUS.includes(a.status)) continue;
    if (hatFreigabe(a.typ)) zahlen[a.typ] += 1;
  }
  return zahlen;
}
